<template>
  <div class="comment-section">
    <!-- 顶部评论输入框 -->
    <div class="comment-input-row">
      <el-input
          v-model="draft"
          type="textarea"
          :rows="2"
          maxlength="500"
          show-word-limit
          placeholder="写下你的留言…">
      </el-input>
      <el-button type="primary" size="small" :loading="sending" @click="submitTop">发送</el-button>
    </div>

    <div class="comment-list">
      <div class="comment-title">💬 留言评论（{{ moment.commentCount || 0 }}）</div>

      <div v-if="comments.length === 0 && !loading" class="comment-empty">还没有留言，来抢沙发吧～</div>
      <div v-if="loading" class="comment-empty">加载中…</div>

      <div v-for="c in comments" :key="c.id" class="comment-block">
        <!-- 顶级评论 -->
        <div class="comment-item top-item">
          <el-avatar :size="30" class="comment-avatar">{{ (c.userName || '星').slice(0, 1) }}</el-avatar>
          <div class="comment-main">
            <div class="comment-name">{{ c.userName }}</div>
            <div class="comment-text">{{ c.content }}</div>
            <div class="comment-meta">
              <span class="comment-time">{{ formatTime(c.createTime) }}</span>
              <span class="comment-action" :class="{ liked: c.isLiked }" @click="toggleLike(c)">
                {{ c.isLiked ? '❤️' : '🤍' }} {{ c.likes || 0 }}
              </span>
              <span class="comment-action" @click="startReply(c)">回复</span>
              <el-dropdown v-if="canDelete(c)" trigger="click" @command="() => delComment(c)" class="comment-dropdown">
                <span class="comment-more">···</span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="delete">删除评论</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>

        <!-- 该评论的回复列表 -->
        <div v-for="r in c.replies" :key="r.id" class="comment-item reply-item">
          <el-avatar :size="26" class="comment-avatar">{{ (r.userName || '星').slice(0, 1) }}</el-avatar>
          <div class="comment-main">
            <div class="comment-name">
              {{ r.userName }}
              <span v-if="r.replyUserName" class="reply-at">回复 @{{ r.replyUserName }}</span>
            </div>
            <div class="comment-text">{{ r.content }}</div>
            <div class="comment-meta">
              <span class="comment-time">{{ formatTime(r.createTime) }}</span>
              <span class="comment-action" :class="{ liked: r.isLiked }" @click="toggleLike(r)">
                {{ r.isLiked ? '❤️' : '🤍' }} {{ r.likes || 0 }}
              </span>
              <span class="comment-action" @click="startReply(r)">回复</span>
              <el-dropdown v-if="canDelete(r)" trigger="click" @command="() => delComment(r)" class="comment-dropdown">
                <span class="comment-more">···</span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="delete">删除评论</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>

          <!-- 回复输入框（内联在对应评论下） -->
          <div v-if="replyAnchorId === r.id" class="reply-input-row">
            <span class="reply-to">回复 @{{ replyTargetName }}</span>
            <el-input v-model="replyDraft" type="textarea" :rows="2" maxlength="500" placeholder="写下你的回复…"></el-input>
            <div class="reply-btns">
              <el-button size="mini" @click="cancelReply">取消</el-button>
              <el-button size="mini" type="primary" :loading="replying" @click="submitReply">发送</el-button>
            </div>
          </div>
        </div>

        <!-- 顶级评论的回复输入框 -->
        <div v-if="replyAnchorId === c.id" class="reply-input-row">
          <span class="reply-to">回复 @{{ replyTargetName }}</span>
          <el-input v-model="replyDraft" type="textarea" :rows="2" maxlength="500" placeholder="写下你的回复…"></el-input>
          <div class="reply-btns">
            <el-button size="mini" @click="cancelReply">取消</el-button>
            <el-button size="mini" type="primary" :loading="replying" @click="submitReply">发送</el-button>
          </div>
        </div>
      </div>

      <!-- 查看更多评论（分页） -->
      <div v-if="remaining > 0" class="more-btn" :class="{ loading: loadingMore }" @click="loadMore">
        查看更多评论（还有 {{ remaining }} 条）
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 评论组件（裙星空间动态评论）
 * - 分页加载，点赞多的评论排在最前
 * - 评论可点赞/取消点赞，可回复，可删除（超管或评论者本人）
 * - 通过 count-change 事件把评论总数变化同步给父级
 */
import { listComments, saveComment, likeComment, deleteComment } from '../../api/moment'
import { formatTime, pad } from '../../utils/format'

const PAGE_SIZE = 5

export default {
  name: 'CommentSection',
  props: {
    moment: { type: Object, required: true },
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      comments: [],       // 已加载的顶级评论（含 replies）
      total: 0,           // 顶级评论总数
      page: 1,
      loading: false,
      loadingMore: false,
      draft: '',          // 顶级评论输入
      sending: false,
      replyAnchorId: null, // 当前回复目标评论ID
      replyTargetName: '',
      replyDraft: '',
      replying: false
    }
  },
  computed: {
    loadedTopCount() {
      return this.comments.length
    },
    remaining() {
      return Math.max(0, this.total - this.loadedTopCount)
    }
  },
  mounted() {
    this.loadPage(1)
  },
  methods: {
    /* ===== 数据加载 ===== */
    loadPage(page, append) {
      this.loading = !append
      if (append) this.loadingMore = true
      listComments(this.moment.id, page, PAGE_SIZE, this.currentNo())
        .then(res => {
          const data = res.data || { total: 0, records: [] }
          this.total = data.total || 0
          if (append) {
            this.comments = this.comments.concat(data.records || [])
          } else {
            this.comments = data.records || []
          }
          this.page = page
        })
        .catch(() => {
          this.$message && this.$message.error('加载评论失败')
        })
        .finally(() => {
          this.loading = false
          this.loadingMore = false
        })
    },
    loadMore() {
      if (this.remaining <= 0 || this.loadingMore) return
      this.loadPage(this.page + 1, true)
    },

    /* ===== 登录判断 ===== */
    currentNo() {
      return this.user && this.user.no ? this.user.no : undefined
    },
    checkLogin() {
      if (!this.user || !this.user.no) {
        this.$emit('need-login')
        return false
      }
      return true
    },

    /* ===== 发表顶级评论 ===== */
    submitTop() {
      if (!this.checkLogin()) return
      if (!this.draft.trim()) {
        this.$message.warning('请输入留言内容')
        return
      }
      this.sending = true
      saveComment({
        momentId: this.moment.id,
        userId: this.user.id,
        userNo: this.user.no,
        userName: this.user.name,
        content: this.draft.trim()
      }).then(res => {
        if (res.code === 200) {
          this.$message.success('留言成功！')
          this.draft = ''
          this.emitCountChange(1)
          // 以服务端数据为准重载第一页（新评论点赞0，排在同页末尾）
          this.loadPage(1)
        } else {
          this.$message.error(res.msg || '留言失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '留言失败')
      }).finally(() => {
        this.sending = false
      })
    },

    /* ===== 回复 ===== */
    startReply(c) {
      if (!this.checkLogin()) return
      this.replyAnchorId = c.id
      this.replyTargetName = c.userName
      this.replyDraft = ''
    },
    cancelReply() {
      this.replyAnchorId = null
      this.replyTargetName = ''
      this.replyDraft = ''
    },
    submitReply() {
      if (!this.checkLogin()) return
      if (!this.replyAnchorId) return
      if (!this.replyDraft.trim()) {
        this.$message.warning('请输入回复内容')
        return
      }
      this.replying = true
      saveComment({
        momentId: this.moment.id,
        userId: this.user.id,
        userNo: this.user.no,
        userName: this.user.name,
        content: this.replyDraft.trim(),
        parentId: this.replyAnchorId,
        replyUserName: this.replyTargetName
      }).then(res => {
        if (res.code === 200) {
          this.$message.success('回复成功！')
          // 归入该回复所属的顶级评论（回复“回复”时同样显示在顶级评论下）
          const top = this.findTopComment(this.replyAnchorId)
          if (top) {
            const replies = top.replies || []
            replies.push({
              id: Date.now(),
              isLocal: true,
              userId: this.user.id,
              userNo: this.user.no,
              userName: this.user.name,
              content: this.replyDraft.trim(),
              replyUserName: this.replyTargetName,
              likes: 0,
              isLiked: false,
              createTime: new Date()
            })
            top.replies = replies
          }
          this.emitCountChange(1)
          this.cancelReply()
        } else {
          this.$message.error(res.msg || '回复失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '回复失败')
      }).finally(() => {
        this.replying = false
      })
    },
    findTopComment(id) {
      for (const c of this.comments) {
        if (c.id === id) return c
        if ((c.replies || []).some(r => r.id === id)) return c
      }
      return null
    },

    /* ===== 评论点赞（点赞后按点赞数重排） ===== */
    toggleLike(c) {
      if (!this.checkLogin()) return
      if (c.isLocal) return this.$message.warning('刚发布的回复请刷新后再点赞')
      likeComment({
        commentId: c.id,
        userId: this.user.id,
        userNo: this.user.no
      }).then(res => {
        if (res.code === 200) {
          c.likes = res.data.likes
          c.isLiked = res.data.liked
          this.resort()
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      }).catch(() => {
        this.$message.error('操作失败，请稍后重试')
      })
    },
    resort() {
      // 顶级评论按点赞数降序、ID升序；回复同理
      const cmp = (a, b) => (b.likes || 0) - (a.likes || 0) || (a.id || 0) - (b.id || 0)
      this.comments.sort(cmp)
      this.comments.forEach(c => {
        if (c.replies && c.replies.length) c.replies.sort(cmp)
      })
    },

    /* ===== 删除评论 ===== */
    canDelete(c) {
      if (!this.user || !this.user.no) return false
      if (this.user.roleId === 0) return true
      return this.user.no === c.userNo
    },
    delComment(c) {
      this.$confirm(`确定删除「${c.userName}」的这条评论吗？`, '删除评论', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
        center: true
      }).then(() => {
        deleteComment({
          id: c.id,
          operatorNo: this.user.no,
          operatorRole: this.user.roleId
        }).then(res => {
          if (res.code === 200) {
            this.$message.success('评论已删除')
            const removedReplies = (c.parentId || 0) === 0 && c.replies ? c.replies.length : 0
            this.removeComment(c.id)
            this.total = Math.max(0, this.total - 1)
            this.emitCountChange(-1 - removedReplies)
            if (this.replyAnchorId === c.id) this.cancelReply()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        }).catch(err => {
          this.$message.error(err.message || '删除失败')
        })
      }).catch(() => {})
    },
    removeComment(id) {
      for (let i = 0; i < this.comments.length; i++) {
        if (this.comments[i].id === id) {
          this.comments.splice(i, 1)
          return
        }
        const replies = this.comments[i].replies || []
        const idx = replies.findIndex(r => r.id === id)
        if (idx >= 0) {
          replies.splice(idx, 1)
          return
        }
      }
    },

    /* ===== 计数同步 ===== */
    emitCountChange(delta) {
      this.$emit('count-change', delta)
    },

    /* ===== 时间格式化（与 Notes 共用 utils/format.js） ===== */
    formatTime,
    pad
  }
}
</script>

<style scoped>
.comment-section {
  width: 100%;
}

/* 顶部评论输入框 */
.comment-input-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 12px;
}

.comment-input-row .el-input {
  flex: 1;
}

.comment-input-row :deep(.el-textarea__inner) {
  font-size: 13px;
  color: #5f3d50;
  border-radius: 10px;
}

/* 评论列表 */
.comment-list {
  border-top: 2px dashed #ffd2e4;
  padding-top: 12px;
}

.comment-title {
  font-size: 14px;
  font-weight: 700;
  color: #c9578b;
  margin-bottom: 8px;
}

.comment-empty {
  font-size: 13px;
  color: #c78ca6;
  padding: 10px 0;
  text-align: center;
}

.comment-block {
  margin-bottom: 2px;
}

.comment-item {
  display: flex;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px dashed #ffe3ef;
}

.reply-item {
  padding-left: 40px;
  background: rgba(255, 240, 247, 0.5);
  border-radius: 10px;
  margin: 4px 0 4px 0;
  border-bottom: none;
}

.comment-avatar {
  flex-shrink: 0;
  background: linear-gradient(135deg, #ffb3d1, #f97fb0);
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

.comment-main {
  flex: 1;
  min-width: 0;
}

.comment-name {
  font-size: 13px;
  font-weight: 700;
  color: #b84f80;
  display: flex;
  align-items: center;
  gap: 6px;
}

.reply-at {
  font-size: 11px;
  font-weight: 400;
  color: #c78ca6;
}

.comment-text {
  font-size: 14px;
  color: #5f3d50;
  line-height: 1.7;
  margin-top: 3px;
  word-break: break-word;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.comment-time {
  font-size: 11px;
  color: #c78ca6;
}

.comment-action {
  font-size: 12px;
  color: #a06b83;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.comment-action:hover {
  color: #e0539c;
}

.comment-action.liked {
  color: #e0539c;
  font-weight: 700;
}

.comment-more {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1;
  color: #a06b83;
  cursor: pointer;
  padding: 1px 2px;
}

.comment-more:hover {
  color: #e0539c;
}

/* 三点菜单靠最右侧 */
.comment-dropdown {
  margin-left: auto;
}

/* 回复输入框 */
.reply-input-row {
  margin: 6px 0 10px 40px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #ffd9e8;
  border-radius: 12px;
}

.reply-to {
  display: block;
  font-size: 12px;
  color: #e0539c;
  margin-bottom: 6px;
}

.reply-input-row :deep(.el-textarea__inner) {
  font-size: 13px;
  color: #5f3d50;
  border-radius: 8px;
}

.reply-btns {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}

/* 查看更多 */
.more-btn {
  text-align: center;
  font-size: 13px;
  color: #e0539c;
  cursor: pointer;
  padding: 10px 0 4px;
  transition: color 0.2s ease;
}

.more-btn:hover {
  color: #c2457c;
  font-weight: 600;
}

.more-btn.loading {
  opacity: 0.6;
  cursor: wait;
}
</style>
